import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { API_BASE_URL } from '@/config'
import {
  Button,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@eshop/shared'
import {
  ArrowRight,
  Check,
  Crown,
  Headphones,
  Heart,
  Home as HomeIcon,
  Package,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Star,
  TrendingUp,
  Truck,
  Wallet
} from 'lucide-react'

interface Product {
  id: number
  name: string
  description: string
  price: string
  primary_image: string | null
  category_details: {
    id: number
    name: string
  } | null
}

export function LandingPage() {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/products/public/featured/`)
        const data = await res.json()
        setFeaturedProducts(data)
      } catch (err) {
        console.error('Error fetching featured products:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchFeatured()
  }, [])

  const productsByCategory = {
    electronics: featuredProducts.filter(p => p.category_details?.name.toLowerCase().includes('electr')),
    fashion: featuredProducts.filter(p => p.category_details?.name.toLowerCase().includes('fashion') || p.category_details?.name.toLowerCase().includes('cloth')),
    home: featuredProducts.filter(p => p.category_details?.name.toLowerCase().includes('home') || p.category_details?.name.toLowerCase().includes('furnit')),
    trending: featuredProducts.slice(0, 3)
  }

  const renderProductCard = (product: Product) => (
    <Card key={product.id} className="group overflow-hidden border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
      <div className="aspect-video relative bg-gradient-to-br from-primary/10 to-secondary/20 overflow-hidden">
        {product.primary_image ? (
          <img
            src={product.primary_image}
            alt={product.name}
            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Package className="h-12 w-12 text-primary opacity-60" />
          </div>
        )}
        <Badge className="absolute top-4 right-4 bg-background/90 text-foreground shadow-sm">Verified</Badge>
      </div>
      <CardHeader className="p-5 pb-3 space-y-2">
        <CardTitle className="line-clamp-2 text-lg leading-snug">{product.name}</CardTitle>
        <CardDescription className="line-clamp-2 text-sm leading-relaxed">
          {product.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-5 pt-0 pb-5 space-y-4">
        <p className="text-2xl font-bold text-foreground">${product.price}</p>
        <Button className="w-full" variant="outline">
          Add to cart
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  )

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-secondary/25 to-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="section-container flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground font-black text-xl shadow-sm">
              N
            </div>
            <span className="text-xl font-bold tracking-tight">nolavi</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-foreground/80">
            <a href="#features" className="hover:text-primary transition-colors">Features</a>
            <a href="#categories" className="hover:text-primary transition-colors">Categories</a>
            <a href="#faq" className="hover:text-primary transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="hidden sm:inline-flex" asChild>
              <Link to="/customer/register">Create account</Link>
            </Button>
            <Button asChild>
              <Link to="/customer/login">
                Log In
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="section-container py-12 sm:py-16">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div className="space-y-8">
            <div className="flex flex-wrap gap-3">
              <Badge className="px-3 py-1.5 text-sm">
                <ShieldCheck className="mr-2 h-4 w-4" />
                Verified Vendors
              </Badge>
              <Badge variant="secondary" className="px-3 py-1.5 text-sm">
                <Sparkles className="mr-2 h-4 w-4" />
                10K+ active shoppers
              </Badge>
              <Badge variant="outline" className="px-3 py-1.5 text-sm">
                <Star className="mr-2 h-4 w-4" />
                4.9 store rating
              </Badge>
            </div>
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]">
                Todo para comprar con seguridad y confianza.
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                Descubre productos únicos de vendedores verificados, envíos rápidos y pagos protegidos.
                Un solo lugar para comprar tranquilo.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" asChild>
                <Link to="#categories">
                  <ShoppingBag className="mr-2 h-5 w-5" />
                  Ver catálogo
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="#features">
                  <Heart className="mr-2 h-5 w-5" />
                  Por qué confiar
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-5">
              {[
                { label: 'Vendedores verificados', value: '500+' },
                { label: 'Productos activos', value: '50K+' },
                { label: 'Satisfacción', value: '4.9★' }
              ].map((item) => (
                <Card key={item.label} className="border bg-white/85 shadow-sm">
                  <CardContent className="p-4 sm:p-6 text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-primary">{item.value}</div>
                    <div className="text-xs sm:text-sm text-muted-foreground font-medium mt-2">
                      {item.label}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

        <Card className="relative overflow-hidden border shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent" />
            <img
              src="/images/hero/hero-image-Fj8ZsRQYldE.jpg"
              alt="Galería comercial iluminada"
              className="h-full w-full object-cover relative"
            />
            <div className="absolute bottom-0 left-0 right-0 p-5 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent text-primary-foreground">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/90 text-primary font-black">
                  N
                </div>
                <div>
                  <p className="text-sm font-semibold">Compras protegidas</p>
                  <p className="text-xs text-primary-foreground/80">Envíos rápidos y devoluciones sencillas</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Star className="h-4 w-4 fill-current" />
                4.9
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t bg-secondary/40">
        <div className="section-container py-12 sm:py-16">
          <div className="text-center space-y-4 max-w-3xl mx-auto mb-14">
            <Badge variant="secondary" className="px-3 py-1.5 text-sm">Seguridad y servicio</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold">Compra sin fricciones</h2>
            <p className="text-muted-foreground text-lg">
              Cubrimos todo el viaje: pagos protegidos, logística fiable y soporte humano cuando lo necesitas.
            </p>
          </div>
          <div className="grid gap-8 lg:gap-10 md:grid-cols-3">
            {[
              {
                icon: ShieldCheck,
                title: 'Protección total',
                desc: 'Pagos seguros, autenticación de vendedores y controles de calidad continuos.',
                points: ['Contracargos protegidos', 'Verificación documental', 'Garantía de autenticidad']
              },
              {
                icon: Truck,
                title: 'Entrega rápida',
                desc: 'Seguimiento en tiempo real y acuerdos logísticos con prioridad de salida.',
                points: ['Tracking minuto a minuto', 'Empaque seguro', 'Opciones express']
              },
              {
                icon: Wallet,
                title: 'Soporte humano',
                desc: 'Ayuda 24/7 en tu idioma y políticas claras de devolución.',
                points: ['Asistencia 24/7', 'Devoluciones simples', 'Guías de compra']
              }
            ].map((item) => (
              <Card key={item.title} className="border bg-white/90 shadow-sm h-full">
                <CardHeader className="space-y-4 pb-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <item.icon className="h-7 w-7" />
                  </div>
                  <div className="space-y-2">
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                    <CardDescription className="text-base leading-relaxed text-muted-foreground">
                      {item.desc}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 pb-6 space-y-3">
                  {item.points.map(point => (
                    <div key={point} className="flex items-center gap-3">
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <Check className="h-3 w-3 text-primary" />
                      </div>
                      <span className="text-sm text-muted-foreground">{point}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="categories" className="section-container py-12 sm:py-16">
        <div className="text-center space-y-4 max-w-3xl mx-auto mb-12">
          <Badge className="px-3 py-1.5 text-sm">Catálogo curado</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold">Explora por categoría</h2>
          <p className="text-muted-foreground text-lg">Elegimos los mejores productos de cada vertical para que ahorres tiempo.</p>
        </div>

        <Tabs defaultValue="electronics" className="w-full">
          <TabsList className="flex flex-wrap gap-2 rounded-2xl bg-secondary/60 p-2">
            <TabsTrigger value="electronics" className="rounded-xl px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Headphones className="mr-2 h-4 w-4" />
              Electrónica
            </TabsTrigger>
            <TabsTrigger value="fashion" className="rounded-xl px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Crown className="mr-2 h-4 w-4" />
              Moda
            </TabsTrigger>
            <TabsTrigger value="home" className="rounded-xl px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <HomeIcon className="mr-2 h-4 w-4" />
              Hogar
            </TabsTrigger>
            <TabsTrigger value="trending" className="rounded-xl px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <TrendingUp className="mr-2 h-4 w-4" />
              Tendencias
            </TabsTrigger>
          </TabsList>

          <TabsContent value="electronics" className="mt-8">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {loading ? (
                <p className="col-span-3 text-center text-muted-foreground py-12">Cargando productos...</p>
              ) : productsByCategory.electronics.length ? (
                productsByCategory.electronics.slice(0, 3).map(renderProductCard)
              ) : (
                <div className="col-span-3 text-center py-12 text-muted-foreground">No hay electrónicos todavía.</div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="fashion" className="mt-8">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {loading ? (
                <p className="col-span-3 text-center text-muted-foreground py-12">Cargando productos...</p>
              ) : productsByCategory.fashion.length ? (
                productsByCategory.fashion.slice(0, 3).map(renderProductCard)
              ) : (
                <div className="col-span-3 text-center py-12 text-muted-foreground">No hay moda disponible.</div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="home" className="mt-8">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {loading ? (
                <p className="col-span-3 text-center text-muted-foreground py-12">Cargando productos...</p>
              ) : productsByCategory.home.length ? (
                productsByCategory.home.slice(0, 3).map(renderProductCard)
              ) : (
                <div className="col-span-3 text-center py-12 text-muted-foreground">No hay productos de hogar aún.</div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="trending" className="mt-8">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {loading ? (
                <p className="col-span-3 text-center text-muted-foreground py-12">Cargando productos...</p>
              ) : productsByCategory.trending.length ? (
                productsByCategory.trending.map(renderProductCard)
              ) : (
                <div className="col-span-3 text-center py-12 text-muted-foreground">No hay tendencias aún.</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t bg-secondary/40">
        <div className="section-container py-12 sm:py-16">
          <div className="text-center space-y-3 mb-10">
            <Badge variant="secondary" className="px-3 py-1.5 text-sm">FAQ</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold">Preguntas frecuentes</h2>
            <p className="text-muted-foreground text-lg">Todo lo esencial antes de tu primera compra.</p>
          </div>
          <Accordion type="single" collapsible className="w-full max-w-3xl mx-auto space-y-4">
            {[
              {
                q: '¿Cómo sé si un vendedor es confiable?',
                a: 'Todos los vendedores pasan verificación de identidad y documentos. Además, auditamos desempeño y reseñas para mantener altos estándares.'
              },
              {
                q: '¿Qué métodos de pago aceptan?',
                a: 'Tarjetas de crédito y débito, wallets digitales y pagos con protección contra fraude.'
              },
              {
                q: '¿Cuánto tarda el envío?',
                a: 'La mayoría de los pedidos llegan entre 3 y 7 días hábiles con tracking en tiempo real.'
              },
              {
                q: '¿Puedo devolver un producto?',
                a: 'Sí, contamos con devoluciones sencillas y reembolsos claros. Consulta la política en cada producto.'
              },
              {
                q: '¿Hay soporte humano?',
                a: 'Disponemos de soporte 24/7 por chat y correo, en tu idioma.'
              }
            ].map((item, idx) => (
              <AccordionItem key={item.q} value={`item-${idx + 1}`} className="border rounded-xl px-4 sm:px-6">
                <AccordionTrigger className="text-left text-base sm:text-lg font-semibold py-4">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base pb-4">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="section-container py-12 sm:py-16">
        <Card className="relative overflow-hidden border shadow-xl">
          <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary" />
          <CardContent className="relative text-primary-foreground px-8 sm:px-12 py-14 sm:py-16 flex flex-col gap-6 sm:gap-8 items-center text-center">
            <div className="space-y-3">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">Empieza hoy</Badge>
              <h3 className="text-3xl sm:text-4xl font-bold">Listo para tu próxima compra segura</h3>
              <p className="text-primary-foreground/80 text-lg max-w-2xl">
                Únete a miles de compradores felices. Crea tu cuenta o inicia sesión y explora el catálogo seleccionado.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90" asChild>
                <Link to="/customer/register">
                  Crear cuenta
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/60 text-white hover:bg-white/10" asChild>
                <Link to="/customer/login">Ya tengo cuenta</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t bg-secondary/30">
        <div className="section-container py-12 sm:py-16">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold">
                  N
                </div>
                <span className="text-lg font-semibold">nolavi</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-sm">
                Marketplace seguro con vendedores verificados, pagos protegidos y envíos confiables.
              </p>
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Compra</h4>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <a href="#categories" className="hover:text-foreground">Catálogo</a>
                <a href="#features" className="hover:text-foreground">Cómo funciona</a>
                <a href="#faq" className="hover:text-foreground">Preguntas frecuentes</a>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Soporte</h4>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <a href="#" className="hover:text-foreground">Ayuda 24/7</a>
                <a href="#" className="hover:text-foreground">Política de devoluciones</a>
                <a href="#" className="hover:text-foreground">Envíos</a>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Compañía</h4>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <a href="#" className="hover:text-foreground">Acerca de</a>
                <a href="#" className="hover:text-foreground">Carreras</a>
                <a href="#" className="hover:text-foreground">Privacidad</a>
              </div>
            </div>
          </div>
          <div className="mt-10 border-t pt-6 text-center text-sm text-muted-foreground">
            © {new Date().getFullYear()} Nolavi. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  )
}
